import { useQuery, UseQueryOptions } from 'react-query';
import { useEvent, client } from '../../';

import { zonedToUtcTimestamp } from '../../utils/dates';
import {
    Activity,
    ActivitiesQueryFilters,
    Location,
    SessionActivity,
    Reactions,
    WaitingRoom,
} from '../types';
import useSpeakers from '../../speakers';
import useSponsors from '../../sponsors';
import useEventSettings from '../../eventSettings';
import { getLocationById } from './helpers';
import { mapSpeakersById, ReshapedSpeakers } from '../../speakers/reshape';
import { mapSponsorsById, ReshapedSponsors } from '../../sponsors/reshape';
import { combineDateStartTimestampWithMinute } from './utils';
import { useFeatureToggle } from '@bizzabo/feature-toggle-client';

export type Session = {
    endDate?: number;
    registrationCapacityEnable: boolean;
    sublocationId?: number;
    enableVirtualSession: boolean;
    registrationCount?: number;
    associatedContacts?: {
        contactListIds: string;
        contactIds: string;
    };
    sponsors?: number[];
    speakers?: {
        role?: string;
        speakerId: number;
    }[];
    enableRecordingSession: boolean;
    reactions?: Reactions;
    waitingRoom?: WaitingRoom;
    chat; // TODO: Type
    exclude?: boolean;
    id: number;
    interestedIn?: string;
    imageType?: string;
    virtualSessionDetails?: {
        eventId: number;
        accountId: number;
        virtualSessionRecordingAuthorizations?: {
            /** Must be one of [TICKET_TYPE, CONTACT_LIST, SESSION_REGISTRATION, PUBLIC] */
            authorizationType: string;
            authorizationValue: string;
        }[];
        sessionId: number;
        virtualSessionAuthorizations?: {
            /** Must be one of [TICKET_TYPE, CONTACT_LIST, SESSION_REGISTRATION, PUBLIC] */
            authorizationType: string;
            authorizationValue: string;
        }[];
        /** Must be one of [VIDEO_PROVIDER, KALTURA, NEWROW, WEBCASTING, EXTERNAL, EMBEDDED, SIMULIVE] */
        type: string;
        ultimateBranding?: {
            backgroundElement?: string;
            backgroundColor?: string;
        };
    };
    eventId?: number;
    registrationVisibility?: boolean;
    recordingIsReady: boolean;
    textColor?: string;
    registration: boolean;
    startDate?: number;
    private: boolean;
    attachments?: {
        fileName?: string;
        icon?: string;
        name: string;
        /** Must be one of [audio, video, image, document] */
        type: string;
        url: string;
    }[];
    hidden: boolean;
    registrationFull?: boolean;
    /** Must be one of [ENABLED, DISABLED] */
    virtualSessionRecordingState: string;
    description?: string;
    registrationCapacity?: number;
    title: string;
    externalSessionAssets?: {
        eventId: number;
        accountId: number;
        sessionId: number;
        id?: number;
        /** Must be one of [YOUTUBE_VIDEO_RECORDING, VIMEO_VIDEO_RECORDING, VIDYARD_VIDEO_RECORDING, LIVESTREAM_VIDEO_RECORDING, EXTERNAL_VIDEO_RECORDING] */
        type: string;
        config: string;
    }[];
    recordingSession?: {
        eventId: number;
        accountId?: number;
        /** Must be one of [ORIGINAL, CUSTOM, EXTERNAL] */
        recordingPrimaryAssetType: string;
        externalAssetId?: number;
        recordingAuthorization?: {
            /** Must be one of [TICKET_TYPE, CONTACT_LIST, SESSION_REGISTRATION, PUBLIC] */
            authorizationType: string;
            authorizationValue: string;
        }[];
        originalAssetId?: number;
        customAssetId?: number;
        recordingIsReady: boolean;
        broadcastRecording: boolean;
        sessionId: number;
        id: number;
    };
    recordingImageUrl?: string;
    attachmentsTitle?: string;
    locationId?: number;
    imageUrl?: string;
    prerollSession?: {
        eventId: number;
        accountId: number;
        futureAssetId?: number;
        assetId?: number;
        sessionId: number;
        id?: number;
    };
    thumbnailUrl?: string;
    moderators?: string;
    endMinute?: number;
    allowRating?: boolean;
    backgroundColor?: string;
    clientId?: string;
    qna?: {
        enable: boolean;
        sessionId: number;
        id: number;
    };
    filters?: {
        id: number;
        tags?: number[];
    }[];
    importId: string;
    previewSize?: number;
    sessionAssets?: {
        assetIsReady: boolean;
        eventId: number;
        accountId: number;
        assetAssociationId: number;
        assetId: string;
        /** Must be one of [VIDEO, IMAGE] */
        assetMedia: string;
        sessionId: number;
        id: number;
        assetIsPrimary: boolean;
        assetMetadata?: string;
        assetUrl: string;
        /** Must be one of [PROVIDER_RECORDING, PRE_RECORDING, RECORDING, PRE_ROLL] */
        assetType: string;
    }[];
    descriptionHtml?: string;
    futureSessionAssets?: {
        eventId: number;
        accountId: number;
        futureAssetId: string;
        futureAssetAssociationId: number;
        /** Must be one of [VIDEO, IMAGE] */
        assetMedia: string;
        sessionId: number;
        id: number;
        assetMetadata?: string;
        futureSessionAssetState: {
            /** Must be one of [UPLOAD_PROCESSING, FUTURE_ASSET_FINISHED, UPLOAD_CANCELED, UPLOAD_FAILED] */
            stateType: string;
            startTime?: string;
            endTime?: string;
        };
        /** Must be one of [PROVIDER_RECORDING, PRE_RECORDING, RECORDING, PRE_ROLL] */
        assetType: string;
    }[];
    startMinute?: number;
    sponsorsTitle?: string;
};

export type SessionAccessErrors = Record<
    number,
    {
        virtualAccessStatus?: 'TICKET_TYPE' | 'CONTACT_LIST' | 'SESSION_REGISTRATION';
        onDemandAccessStatus?: 'TICKET_TYPE' | 'SESSION_REGISTRATION';
    }
>;

export type ActivitiesResponseType = {
    sublocationsAggregations?: {
        count: number;
        sublocationId: number;
    }[];
    locationsAggregations?: {
        locationId: number;
        count: number;
    }[];
    total: number;
    sessions?: Session[];
    offset: number;
    count?: number;
    filterTagAggregation?: {
        tagId: number;
        count: number;
    }[];
    sessionAccessErrors: SessionAccessErrors;
};
type RequestBodyType = {
    broadcastingNow?: boolean;
    offset?: number;
    count?: number;
    sublocations?: string;
    dates?: string;
    sort?: string;
    filters?: string;
    endDateTime?: number;
    sessionsIds?: string;
    q?: string;
    startDateTime?: number;
    /** Must be one of [asc, desc] */
    sortOrder?: string;
    speakers?: string;
    registration?: boolean;
    locations?: string;
    startTime?: number;
    endTime?: number;
    aggregations: boolean;
};

export async function fetchActivities(
    eventId: number,
    requestBody: RequestBodyType
): Promise<ActivitiesResponseType> {
    return (await client().post(`/api/v2/agenda/events/${eventId}/sessions`, requestBody))
        .data as ActivitiesResponseType;
}

export async function fetchUserActivities(
    eventId: number,
    requestBody: RequestBodyType
): Promise<ActivitiesResponseType> {
    return (await client().post(`/api/v2/agenda/events/${eventId}/userSessions`, requestBody))
        .data as ActivitiesResponseType;
}

type HydrateActivityType = (
    activity: Session,
    allSpeakers: ReshapedSpeakers,
    allSponsors: ReshapedSponsors,
    timeZone: string,
    locations: Record<number, Location>,
    eventId: number
) => SessionActivity;

export const hydrateActivity: HydrateActivityType = (
    activity,
    allSpeakers,
    allSponsors,
    timeZone,
    locations,
    eventId
) => {
    const {
        id,
        title,
        startDate,
        startMinute,
        endDate,
        endMinute,
        thumbnailUrl,
        speakers: speakersIdsAndRole,
        sponsors: sponsorsIds,
        sponsorsTitle,
        previewSize,
        filters,
        attachments,
        attachmentsTitle,
        description,
        descriptionHtml,
        locationId,
        sublocationId,
        enableVirtualSession,
        qna,
        reactions,
        waitingRoom,
        chat,
        enableRecordingSession,
        recordingSession,
        registration,
        registrationCapacityEnable,
        registrationFull,
        registrationVisibility,
        registrationCapacity,
        virtualSessionDetails,
    } = activity;

    const activityStartTimestamp = combineDateStartTimestampWithMinute(startDate, startMinute);
    const activityEndTimestamp = combineDateStartTimestampWithMinute(endDate, endMinute);

    return {
        id,
        type: 'SESSION',
        title,
        startDate: activityStartTimestamp, // TODO: remove
        endDate: activityEndTimestamp, // TODO: remove
        startDateUTCTimestamp:
            (!!activityStartTimestamp &&
                !Number.isNaN(activityStartTimestamp) &&
                zonedToUtcTimestamp(activityStartTimestamp, timeZone)) ||
            undefined,
        endDateUTCTimestamp:
            (!!activityEndTimestamp &&
                !Number.isNaN(activityEndTimestamp) &&
                zonedToUtcTimestamp(activityEndTimestamp, timeZone)) ||
            undefined,
        thumbnailUrl,
        speakers: mapSpeakersById(speakersIdsAndRole, allSpeakers),
        sponsors: mapSponsorsById(sponsorsIds, allSponsors),
        sponsorsTitle,
        previewSize,
        filters,
        attachments,
        attachmentsTitle,
        description,
        descriptionHtml,
        location: getLocationById(locationId, sublocationId, locations),
        enableVirtualSession,
        qna,
        reactions,
        waitingRoom,
        chat,
        isScheduled: !!activityStartTimestamp && !!activityEndTimestamp,
        eventId,
        enableRecordingSession,
        recordingSession,
        registration,
        registrationCapacityEnable,
        registrationFull,
        registrationVisibility,
        registrationCapacity,
        virtualSessionDetails,
    };
};

type HydrateActivitiesType = (
    activities: ActivitiesResponseType,
    allSpeakers: ReshapedSpeakers,
    allSponsors: ReshapedSponsors,
    timeZone: string,
    locations: Record<number, Location>,
    eventId: number
) => Array<Activity | SessionActivity> | null;

const hydrateActivities: HydrateActivitiesType = (
    activities,
    allSpeakers,
    allSponsors,
    timeZone,
    locations,
    eventId
) => {
    return activities.sessions.map(activity =>
        hydrateActivity(activity, allSpeakers, allSponsors, timeZone, locations, eventId)
    );
};

type UseActivitiesType = (
    eventUniqueName: string,
    filters?: ActivitiesQueryFilters,
    options?: UseQueryOptions<ActivitiesResponseType>
) => {
    data: Array<Activity | SessionActivity> | null;
    error: Error;
    isLoading: boolean;
    isIdle: boolean;
    isSuccess: boolean;
};

export const useRawActivities = <TData = ActivitiesResponseType>(
    eventId: number,
    options?: UseQueryOptions<ActivitiesResponseType, Error, TData>
) => {
    const isSessionVisibilityOn = useFeatureToggle('session-visibility');

    const rawActivitiesQuery = useQuery(
        ['raw_activities', eventId],
        () =>
            isSessionVisibilityOn
                ? fetchUserActivities(eventId, { aggregations: true })
                : fetchActivities(eventId, { aggregations: true }),
        options
    );
    return rawActivitiesQuery;
};

export const useSessionAccessErrors = (eventUniqueName: string) => {
    const event = useEvent(eventUniqueName);
    const eventId = event?.data?.id;

    const { data: sessionAccessErrors } = useRawActivities<SessionAccessErrors>(eventId, {
        select: data => data.sessionAccessErrors,
        notifyOnChangeProps: ['data'],
        enabled: !!eventId,
    });

    return sessionAccessErrors;
};

const useActivities: UseActivitiesType = (
    eventUniqueName,
    _,
    options: UseQueryOptions<ActivitiesResponseType> = {}
) => {
    const event = useEvent(eventUniqueName);
    const eventId = event?.data?.id;

    // We are checking here if there's one more condition for if the query is enabled
    const enabled = options.enabled == null || options.enabled;

    const activitiesQuery = useRawActivities(eventId, {
        ...options,
        staleTime: 1000 * 60 * 10, // 10 minutes in ms
        enabled: !!eventId && enabled,
    });

    const speakersQuery = useSpeakers(eventId);
    const sponsorsQuery = useSponsors(eventId);
    const eventSettings = useEventSettings(eventUniqueName);

    return useQuery(
        ['hydrated_activities', eventId],
        async () =>
            hydrateActivities(
                activitiesQuery.data,
                speakersQuery.data,
                sponsorsQuery.data,
                event.data?.timeZone,
                eventSettings.data.locations,
                eventId
            ),

        {
            enabled:
                activitiesQuery.isSuccess &&
                speakersQuery.isSuccess &&
                sponsorsQuery.isSuccess &&
                eventSettings.isSuccess &&
                !!event.data?.timeZone,
        }
    );
};

export const useGetFirstActivity = (
    eventUniqueName: string,
    options: UseQueryOptions<ActivitiesResponseType> = {}
) => {
    const enabled = options.enabled == null || options.enabled;
    const { data: activities, ...queryRes } = useActivities(eventUniqueName, null, {
        enabled: enabled,
    });
    return { data: activities?.length ? activities[0] : null, ...queryRes };
};

export default useActivities;
